import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import AdminImages from './AdminImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDate(iso){
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminDashboard(){
  const nav = useNavigate();
  const [tab, setTab] = useState('enquiries');
  const [enquiries, setEnquiries] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('nc_admin_token') : null;
  const email = typeof window !== 'undefined' ? localStorage.getItem('nc_admin_email') : '';

  const logout = () => {
    localStorage.removeItem('nc_admin_token');
    localStorage.removeItem('nc_admin_email');
    nav('/admin/login');
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [e, q] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, { headers }),
        axios.get(`${API}/admin/quiz`, { headers }),
      ]);
      setEnquiries(e.data.items || []);
      setQuizzes(q.data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else setError(err?.response?.data?.detail || 'Failed to load data');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) { nav('/admin/login'); return; }
    load();
  }, [token, load, nav]);

  return (
    <div className="admin-shell">
      <Helmet>
        <title>Admin — Nomad Compass</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="admin-wrap">
        <div className="admin-header">
          <div>
            <Link to="/" className="brand" style={{ marginBottom: 12 }}>
              <img className="brand-logo" src="/nomad-compass-logo.png" alt="Nomad Compass" width="800" height="250" />
            </Link>
            <h1>Admin dashboard</h1>
            <div style={{ color: 'var(--warm-grey)', fontSize: 13, marginTop: 4 }}>Signed in as {email}</div>
          </div>
          <button className="admin-logout" onClick={logout} data-testid="admin-logout">Sign out</button>
        </div>

        <div className="admin-tabs">
          <button className={`admin-tab ${tab === 'enquiries' ? 'active' : ''}`} onClick={() => setTab('enquiries')} data-testid="tab-enquiries">Enquiries ({enquiries.length})</button>
          <button className={`admin-tab ${tab === 'quiz' ? 'active' : ''}`} onClick={() => setTab('quiz')} data-testid="tab-quiz">Quiz submissions ({quizzes.length})</button>
          <button className={`admin-tab ${tab === 'images' ? 'active' : ''}`} onClick={() => setTab('images')} data-testid="tab-images">Country images</button>
          <button className="admin-tab" onClick={load}>↻ Refresh</button>
        </div>

        {error && <div className="admin-error">{error}</div>}
        {loading && <div className="admin-empty">Loading…</div>}

        {!loading && tab === 'enquiries' && (
          enquiries.length === 0 ? <div className="admin-empty">No enquiries yet.</div> : (
            <table className="admin-table" data-testid="table-enquiries">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Nationality</th>
                  <th>Income</th>
                  <th>Family</th>
                  <th>Countries</th>
                  <th>Source</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map(e => (
                  <tr key={e.id}>
                    <td>{fmtDate(e.created_at)}</td>
                    <td><strong>{e.name}</strong></td>
                    <td>{e.email}{e.phone ? <div style={{ color: 'var(--muted)', fontSize: 12 }}>{e.phone}</div> : null}</td>
                    <td>{e.nationality || '—'}<div style={{ color: 'var(--muted)', fontSize: 12 }}>{e.current_location}</div></td>
                    <td>{e.income_range || '—'}</td>
                    <td>{e.family_situation || '—'}</td>
                    <td>{e.countries_considering || '—'}</td>
                    <td>{e.page_source || '—'}</td>
                    <td style={{ maxWidth: 240 }}>{e.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {!loading && tab === 'quiz' && (
          quizzes.length === 0 ? <div className="admin-empty">No quiz submissions yet.</div> : (
            <table className="admin-table" data-testid="table-quiz">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Income</th>
                  <th>Work</th>
                  <th>Family</th>
                  <th>Region</th>
                  <th>Recommendations</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {quizzes.map(q => (
                  <tr key={q.id}>
                    <td>{fmtDate(q.created_at)}</td>
                    <td>{q.monthly_income_usd ? `$${q.monthly_income_usd}/mo` : '—'}</td>
                    <td>{q.work_type || '—'}</td>
                    <td>{q.family || '—'}</td>
                    <td>{q.region_preference || '—'}</td>
                    <td>{(q.recommendations || []).join(', ') || '—'}</td>
                    <td>{q.email || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {tab === 'images' && <AdminImages />}
      </div>
    </div>
  );
}
